import { PerformanceTracker } from "../PerformanceTracker";
import { KarrotAnalyticsTransporter } from "../webview/KarrotAnalyticsTransporter";
import { observeAppScript } from "./appScriptLoad";
import { sampleBridgeState } from "./bridgeState";
import { buildFirstLoadTiming } from "./buildFirstLoadTiming";
import {
  collectApiRequestTimings,
  collectImageSummary,
  collectResourceTimings,
} from "./collectApiRequestTimings";
import { installFirstApiRequestHook } from "./firstApiRequestHook";
import { observeFirstApi, observeResourceTimings } from "./firstApiTiming";
import { observeFirstScript } from "./firstScriptEval";
import { observeIndexScript } from "./indexScriptLoad";
import { startPersonalCapture } from "./personalCapture";
import { registerSelfScript } from "./registerSelfScript";

const EVENT_NAME = "client_impressed_common_xview_screen";

// Kill switch: flip to false and rebuild to disable the request hook without a full revert.
const ENABLE_REQUEST_HOOK = true;

if (!startPersonalCapture()) {
  registerSelfScript();
  observeFirstScript();

  let performanceTracker: PerformanceTracker | undefined;

  // Install BEFORE the tracker is constructed so the hook sees the very first fetch/XHR call.
  // App code runs strictly after this <head> bundle finishes evaluating.
  if (ENABLE_REQUEST_HOOK) {
    installFirstApiRequestHook({
      onFirstApi: () => {
        performanceTracker?.notifyFirstApiReady();
      },
    });
  }

  const transporter = new KarrotAnalyticsTransporter(EVENT_NAME);
  performanceTracker = new PerformanceTracker({
    location: window.location,
    transporter,
  });

  observeIndexScript({
    onLoad: () => {
      performanceTracker.notifyEntryScriptReady();
    },
  });
  observeAppScript({
    onLoad: () => {
      performanceTracker.notifyBundleScriptReady();
    },
  });
  observeFirstApi({
    onFirstApi: () => {
      performanceTracker.notifyFirstApiReady();
    },
  });
  // Accumulate API + static-resource requests (eviction-proof) for the
  // LCP-cutoff waterfalls.
  observeResourceTimings();

  // If the hook already captured before the tracker existed, notify now so the iOS deadline path reacts.
  if (window.__xviewPerfTiming__?.firstApiRequestStartTime != null) {
    performanceTracker.notifyFirstApiReady();
  }

  performanceTracker.report({
    getFirstLoadTiming: () => buildFirstLoadTiming(window.__xviewPerfTiming__),
    getApiRequestTimings: (cutoffTime) => collectApiRequestTimings(cutoffTime),
    getResourceTimings: (cutoffTime) => collectResourceTimings(cutoffTime),
    getImageSummary: (cutoffTime) => collectImageSummary(cutoffTime),
    // Sampled on the way out of every flush — see bridgeState.ts for why this is
    // a sampler and not a poller.
    getBridgeState: () => sampleBridgeState(),
  });
}

// Build-time injected by esbuild `define`. Also emitted as a `/*! ... */`
// banner so the version is readable from the raw CDN file without executing it.
declare const __XVIEW_PERF_VERSION__: string;

export const version =
  typeof __XVIEW_PERF_VERSION__ !== "undefined"
    ? __XVIEW_PERF_VERSION__
    : "dev";
